import React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import FirebaseService from "../../../services/firebase/FirebaseService";
import "./BookScreen.css";
import { Container, Drawer, Hidden, Typography, withStyles, WithStyles } from "@material-ui/core";
import BookCard from "../StudentHomeScreen/BookCard";
import BookScreenQueueCard from "./QueueCards/BookScreenQueueCard";
import BookScreenComprehensionCard from "./BookScreenComprehensionCard";
import ReaderView from "./ReaderPage";
import BookScreenBanner from "./BookScreenBanner";
import { connect } from "react-redux";
import { RootState } from "../../../state/configureStore";
import TeacherAudioCard from "./TeacherAudioCard";
import {
  advanceRecordingSection,
  resetRecordingSection,
  setCurrentQuestion,
  setPage,
  startRecording,
  stopRecording,
} from "./state/BookScreenState";
import RecorderService from "../../../services/recorder/RecorderService";
import LogService from "../../../services/LogService";
import UploadService from "../../../services/upload/UploadService";
import ComprehensionQuestionView from "./ComprehensionQuestionView";
import ComprehensionQuestion from "../../../interfaces/ComprehensionQuestion";
import { ProviderContext, withSnackbar } from "notistack";
import { Redirect, Route } from "react-router-dom";
import UserSettings from "../../../interfaces/UserSettings";
import BookScreenService from "./state/BookScreenService";
import Loading from "../../Loading/Loading";
import ConfirmationModal from "../../Modals/ConfirmationModal";
import { closeInformationModal, showErrorModal } from "../../Modals/InformationModalReducer";
import InformationModal from "../../Modals/InformationModal";
import ReadingPaneButtons from "./ReadingPaneButtons";
import { Theme } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";
import MobileReadingPaneBanner from "./MobileReadingPaneBanner";
import AnalyticsService from "../../../services/analytics/AnalyticsService";
import TeacherInstructionCard from "./TeacherInstructionCard";
import ButtonWithLoading from "../../Loading/ButtonWithLoading";
import LoadingButton from "../../Loading/LoadingButton";
import BookScreenBannerTeacher from "./BookScreenBannerTeacher";
import { emptyBookResult } from "../StudentHomeScreen/state/BookResults";

const styles = (theme: Theme) => ({
  root: {
    width: "380px",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
    [theme.breakpoints.only("sm")]: {
      boxShadow: "10px 5px 5px grey",
    },
  },
  drawer: {
    width: "100%",
    flexShrink: 0,
  },
  drawerPaper: {
    width: "100%",
  },
});

interface ScreenState {
  hasRecordedThisPage: boolean;
  timerOn: boolean;
  timerStart: number;
  timerTime: number;
  deletePromptOpen: boolean;
  deletePromptContinueRecording: boolean;
  drawerOpen: boolean;
  loadingButtonState: LoadingButton;
}

type BookScreenProps = ReturnType<typeof mapStateToProps> &
  RouteComponentProps &
  ProviderContext &
  WithStyles<typeof styles>;
class BookScreen extends React.Component<BookScreenProps, ScreenState> {
  recorderService: RecorderService;
  service = new BookScreenService();
  timerInterval: ReturnType<typeof setInterval> | undefined;
  _hasStartedLoad = false;

  get isPreview(): boolean {
    return new URLSearchParams(this.props.location.search).get("isPreview") === "true";
  }

  get classId(): string {
    return new URLSearchParams(this.props.location.search).get("classid") || "";
  }

  constructor(props: BookScreenProps) {
    super(props);
    this.state = {
      ...this.state,
      timerOn: false,
      timerStart: 0,
      timerTime: 0,
      deletePromptOpen: false,
      deletePromptContinueRecording: false,
      loadingButtonState: LoadingButton.BeforeLoading,
    };
    this.recorderService = new RecorderService();
  }

  override componentDidMount() {
    // Log the screen view
    AnalyticsService.logScreenView();

    this.recorderService.initialize(
      45 * 1000,
      this.props.audioDevice,
      async (data, duration) => {
        //when this triggers, need to restart the timer with all the offsets.
        // Upload to the server
        if (this.state.timerOn && this.props.isRecording) {
          this.stopTimer();
          this.resetTimer();
          this.startTimer();
        }

        const page = this.props.currentPage;
        const recordingSectionNumber = this.props.recordingSectionNumber;

        advanceRecordingSection();

        // Move it to its own thread so that it doesn't hold up the student
        new Promise<void>(async (resolve, reject) => {
          try {
            const uploadPath = await FirebaseService.initializeSubmissionPage(
              this.props.userId,
              this.props.assignmentState.book.id,
              page.navigation.id,
              this.classId,
              page.content,
            );

            await FirebaseService.initializeRecordingPage(
              this.props.userId,
              this.props.assignmentState.assignment.bookId,
              page.navigation.id,
              recordingSectionNumber,
            );

            await UploadService.uploadAudioData(
              data,
              uploadPath + `/s${recordingSectionNumber}`,
              Math.floor(duration / 100) / 10,
            );

            await FirebaseService.submitRecordingPage(
              this.props.userId,
              this.props.assignmentState.assignment.bookId,
              page.navigation.id,
            );

            resolve();
          } catch (e) {
            reject(e);
          }
        }).catch(e => {
          LogService.exception(e, `BookScreen.startRecording`, "Failed to initialize recording page");
          showErrorModal("Recording Failed", "Could not upload your recording");
        });
      },
      () => {
        stopRecording();
        showErrorModal("Recording Failed", "Could not record audio.");
      },
    );
  }

  override componentWillUnmount() {
    if (this.props.isRecording) {
      this.stopRecording(false).catch(err =>
        LogService.exception(err, `BookScreen.componentWillUnmount}`, "Failed to stop the recording"),
      );
    }

    this.service.cleanup();
    this.recorderService.cleanupAudio();
  }

  startTimer = () => {
    this.setState({
      timerOn: true,
      timerStart: Date.now() - this.state.timerTime,
    });
    this.timerInterval = setInterval(() => {
      this.setState({
        timerTime: Date.now() - this.state.timerStart,
      });
    }, 1000);
  };

  stopTimer = () => {
    this.setState({ timerOn: false });
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  };

  resetTimer = () => {
    this.setState({
      timerStart: 0,
    });
  };

  openDrawer() {
    this.setState(state => {
      return {
        ...state,
        drawerOpen: true,
      };
    });
  }

  closeDrawer() {
    this.setState(state => {
      return {
        ...state,
        drawerOpen: false,
      };
    });
  }

  getTimerDuration(time: number): string {
    const seconds = ("0" + (Math.floor(time / 1000) % 60)).slice(-2);
    const minutes = ("0" + (Math.floor(time / 60000) % 60)).slice(-2);
    return minutes + " : " + seconds;
  }

  async startRecording() {
    this.setState({ hasRecordedThisPage: true });
    this.startTimer();

    startRecording();
    this.recorderService.startRecording();
  }

  async stopRecording(showMessage: boolean) {
    this.stopTimer();

    stopRecording();
    await this.recorderService.stopRecording();
    if (showMessage) {
      this.props.enqueueSnackbar("Recording Stopped", { variant: "success", autoHideDuration: 5000 });
    }
  }

  async deleteRecording() {
    this.stopTimer();
    this.resetTimer();
    this.setState({
      timerTime: 0,
    });

    if (this.props.isRecording) {
      stopRecording();
      this.stopTimer();
      await this.recorderService.stopRecording();
    }

    await FirebaseService.deleteRecordingPage(
      this.props.userId,
      this.props.assignmentState.assignment.bookId,
      this.props.currentPage.navigation.id,
    );

    resetRecordingSection();

    this.props.enqueueSnackbar("Recording Deleted", { variant: "success", autoHideDuration: 5000 });
  }

  isLastPage(): boolean {
    return (
      this.props.assignmentState.pages && this.props.currentPageNumber === this.props.assignmentState.pages.length - 1
    );
  }

  openConfirmDelete(continueRecording: boolean) {
    this.setState(state => {
      return {
        ...state,
        deletePromptOpen: true,
        deletePromptContinueRecording: continueRecording,
      };
    });
  }

  closeConfirmDelete() {
    this.setState(state => {
      return {
        ...state,
        deletePromptOpen: false,
      };
    });
  }

  isOnComprehensionQuestion() {
    return (
      this.props.currentQuestionId !== "" &&
      this.props.assignmentState.assignment?.assignmentComments?.comprehensionQuestions?.find(
        question => question.id === this.props.currentQuestionId,
      )
    );
  }

  async setRecordingPageComplete() {
    await FirebaseService.recordedPageComplete(
      this.props.userId,
      this.props.assignmentState.assignment.bookId,
      this.props.currentPage.navigation.id,
    );
  }

  async setRecordingPageIncomplete() {
    await FirebaseService.recordedPageIncomplete(
      this.props.userId,
      this.props.assignmentState.assignment.bookId,
      this.props.currentPage.navigation.id,
    );
  }

  async onNext() {
    // If we're recording, stop it for a submission
    // then start it up again
    if (this.props.isRecording) {
      this.setRecordingPageComplete().then(() => {
        // NO-OP
      });
      await this.stopRecording(false);
      setPage(this.props.currentPageNumber + 1);
      await this.startRecording();
    } else {
      this.setState(state => {
        return { ...state, hasRecordedThisPage: false };
      });
      setPage(this.props.currentPageNumber + 1);
    }
  }

  async onSubmit() {
    if (this.props.isRecording) {
      await this.stopRecording(false);
      this.setRecordingPageComplete().then(() => {
        this.props.enqueueSnackbar("Recording Submitted", {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            horizontal: "center",
            vertical: "top",
          },
        });
      });
    }
  }

  override render() {
    // If we're loading the current user, just return a loading spinner
    if (this.props.currentUserState.isConnectingUser || this.props.currentUserState.isConnectingUser) {
      return <Loading />;
    }

    if (this.props.userId && !this._hasStartedLoad) {
      this._hasStartedLoad = true;

      const params = new URLSearchParams(this.props.location.search);
      const assignmentId = params.get("assignmentid");

      if (assignmentId) {
        this.service.watchAssignment(this.props.userId, assignmentId);
      }

      return <Loading />;
    }

    // Ensure the user is logged in
    if (!this.props.currentUserState.currentUser) {
      return <Route component={() => <Redirect to={{ pathname: "/Login" }} />} render={undefined} />;
    }

    if (!this.service.loaded) {
      return <Loading />;
    }

    if (this.props.loadingError && this.props.loadingError !== "") {
      return <div>{this.props.loadingError}</div>;
    }

    const bookResults =
      this.props.homeScreenState.studentBooks[this.props.assignmentState.assignment.id] || emptyBookResult();

    const showAddToAssignmentsButton =
      this.isPreview && this.props.assignmentState.assignment?.parentId === this.props.adminId;

    const currentQuestion = this.props.assignmentState.assignment?.assignmentComments?.comprehensionQuestions?.find(
      question => question.id === this.props.currentQuestionId,
    );

    return (
      <div className="bookScreen">
        <div className="BookScreenContent">
          <Hidden smDown>
            {showAddToAssignmentsButton ? (
              <BookScreenBannerTeacher assignment={this.props.assignmentState.assignment} />
            ) : (
              <BookScreenBanner
                title={this.props.assignmentState.assignment?.title as string}
                totalWords={bookResults.wordCount}
                uniqueWords={bookResults.uniqueWordCount}
                currentUserSettings={this.props.currentUserState.currentUserSettings as UserSettings}
                currentUser={this.props.currentUserState.currentUser}
              />
            )}
          </Hidden>
          <div className="entirePage">
            <div className={`${this.props.classes.root} leftColumn`}>
              <Hidden mdUp>
                {showAddToAssignmentsButton ? (
                  <BookScreenBannerTeacher assignment={this.props.assignmentState.assignment} />
                ) : (
                  <BookScreenBanner
                    title={this.props.assignmentState.assignment?.title as string}
                    totalWords={bookResults.wordCount}
                    uniqueWords={bookResults.uniqueWordCount}
                    currentUserSettings={this.props.currentUserState.currentUserSettings as UserSettings}
                    currentUser={this.props.currentUserState.currentUser}
                  />
                )}
              </Hidden>
              <Container className="topLeftContainer">
                <BookCard
                  bookName={this.props.assignmentState.bookTitle}
                  coverImageUrl={this.props.assignmentState.book?.coverImageUrl}
                  className={bookResults.className}
                  wordCount={bookResults.wordCount}
                  uniqueWordCount={bookResults.uniqueWordCount}
                  estimatedTime={bookResults.estimatedTime}
                  isNew={bookResults.isNew}
                  accuracy={bookResults.accuracy}
                  wordsPerMinute={bookResults.wordsPerMinute}
                  completion={bookResults.completion}
                  studentFacingData={this.props.assignmentState.assignment.studentFacingData}
                  actualTime={bookResults.actualTime}
                />

                {this.props.assignmentState.assignment?.assignmentComments?.introAudio?.audioPath && (
                  <TeacherAudioCard
                    audioPath={
                      this.props.assignmentState.assignment?.assignmentComments?.introAudio?.audioPath as string
                    }
                    isInstructions
                  />
                )}
                {this.props.assignmentState.assignment?.assignmentComments?.modelTextAudio?.audioPath && (
                  <TeacherAudioCard
                    audioPath={this.props.assignmentState.assignment?.assignmentComments?.modelTextAudio?.audioPath}
                  />
                )}
              </Container>
              <Container className="studentActionList">
                {this.props.assignmentState.assignment?.assignmentComments?.introAudio?.textComment && (
                  <TeacherInstructionCard
                    instructions={
                      this.props.assignmentState.assignment?.assignmentComments?.introAudio?.textComment as string
                    }
                  />
                )}
                {this.props.assignmentState.pages?.map((page, index) => {
                  const recordedPage = this.props.assignmentState.recordedPages.find(
                    recordedPage => recordedPage.pageId === page.navigation.id,
                  );
                  const isSubmitted = recordedPage !== undefined;
                  const isActive =
                    this.props.currentPage.navigation.id === page.navigation.id && this.props.currentQuestionId === "";
                  return (
                    <div key={page.navigation.id} className="cardList">
                      <BookScreenQueueCard
                        isActive={isActive}
                        pageTitle={page.title}
                        onStartRecording={() => {
                          this.startRecording();
                          this.openDrawer();
                        }}
                        pageIndex={index}
                        completePage={async () => {
                          await this.setRecordingPageComplete();
                        }}
                        incompletePage={async () => {
                          await this.setRecordingPageIncomplete();
                        }}
                        onStopRecording={() => this.stopRecording(true)}
                        onDeleteRecording={() => this.openConfirmDelete(false)}
                        timeRecorded={this.getTimerDuration(this.state.timerTime)}
                        recordedPage={recordedPage}
                        isSubmitted={isSubmitted}
                        wordsPerMin={recordedPage && this.service.roundWpm(recordedPage.wordsPerMin)}
                        accuracy={recordedPage && recordedPage.accuracy}
                        onSetPage={async pageIndex => {
                          if (this.props.isRecording) {
                            await this.stopRecording(false);
                          }
                          setPage(pageIndex);
                        }}
                        studentFacingData={this.props.assignmentState.assignment.studentFacingData}
                      />
                    </div>
                  );
                })}
                {this.props.assignmentState.assignment?.assignmentComments?.comprehensionQuestions?.map(
                  (question: ComprehensionQuestion, index: number) => (
                    <div className="cardList" key={question.id}>
                      <BookScreenComprehensionCard
                        pageTitle={"Comprehension Question #" + (index + 1)}
                        audioPath={question.audioPath}
                        id={question.id}
                        onClick={(questionId: string) => {
                          setCurrentQuestion(questionId);
                          this.openDrawer();
                        }}
                      />
                    </div>
                  ),
                )}
                <ButtonWithLoading
                  className="turnInButton"
                  buttonClickText={bookResults.turnedIn ? "Resubmit Assignment" : "Turn in Assignment"}
                  buttonFinishedText="Submitted"
                  loadingButtonState={this.state.loadingButtonState}
                  changeLoadingButtonState={newLoadingState => this.setState({ loadingButtonState: newLoadingState })}
                  clickHandler={async () => {
                    if (this.props.isRecording) {
                      await this.stopRecording(false);
                    }
                    await FirebaseService.turnInSubmission(
                      this.props.userId,
                      this.props.assignmentState.assignment.bookId,
                    );
                    this.props.history.replace("/Home");
                  }}
                ></ButtonWithLoading>
              </Container>
            </div>

            {/* Normal screen reading pane */}
            <Hidden smDown>
              <div className="rightColumn">
                <div className="contentView">
                  {this.props.assignmentState.book.viewType !== "external" && (
                    <Typography className="pageTitle">
                      {this.props.assignmentState.pages &&
                        this.props.currentQuestionId === "" &&
                        this.props.assignmentState.book?.name}
                    </Typography>
                  )}
                  <Typography className="comprehensionTitle">
                    {this.props.currentQuestionId !== "" && "Comprehension Question"}
                  </Typography>
                  <div className="contentViewText">
                    {this.props.assignmentState.pages && this.props.currentQuestionId === "" && (
                      <ReaderView
                        pdfPath={this.props.assignmentState.book.pdfPath}
                        fontSize={Math.max(
                          (this.props.currentUserState.currentUserSettings?.readerFontSize || 28) as number,
                          8,
                        )}
                        viewType={this.props.assignmentState.book.viewType}
                        bookName={this.props.assignmentState.book.name}
                        pageNumber={this.props.currentPageNumber}
                        page={this.props.assignmentState.pages[this.props.currentPageNumber]}
                        correctString={
                          !this.props.isRecording ? this.props.currentRecordedPage?.unreadPortionWithErrors : ""
                        }
                      />
                    )}
                    {this.props.currentQuestionId !== "" && currentQuestion && (
                      <ComprehensionQuestionView
                        classId={this.classId}
                        assignmentId={this.props.assignmentState?.book?.id}
                        currentUserId={this.props?.currentUserState?.currentUser?.uid}
                        replyType={this.props.assignmentState.assignment?.assignmentComments?.replyType}
                        question={currentQuestion}
                      />
                    )}
                  </div>

                  {this.props.assignmentState.pages && this.props.currentQuestionId === "" && (
                    <ReadingPaneButtons
                      isRecording={this.props.isRecording}
                      isLastPage={this.isLastPage()}
                      hasRecordedPage={this.state.hasRecordedThisPage}
                      onDelete={() => {
                        this.openConfirmDelete(true);
                      }}
                      onNext={async () => {
                        await this.onNext();
                      }}
                      onSubmit={async () => {
                        this.closeDrawer();
                        await this.onSubmit();
                      }}
                    />
                  )}
                </div>
              </div>
            </Hidden>

            {/* Mobile version of the reader pane */}
            <Hidden xsDown mdUp>
              <div className="skeleton">
                <Skeleton variant="text" animation={false} height="33px" width="64px" />
                <br />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <br />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <br />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <br />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <Skeleton variant="text" animation={false} />
                <br />
              </div>
            </Hidden>
            <Hidden mdUp>
              <Drawer
                onClose={async () => {
                  this.closeDrawer();
                  if (this.props.isRecording) {
                    await this.stopRecording(true);
                  }
                }}
                // className={this.props.classes.drawer}
                classes={{ paper: this.props.classes.drawerPaper }}
                open={this.state.drawerOpen}
                anchor="right"
              >
                <MobileReadingPaneBanner
                  pageTitle={
                    this.isOnComprehensionQuestion()
                      ? "Comprehension Question"
                      : this.props.currentPage?.title || `Page ${this.props.currentPageNumber + 1}`
                  }
                  onClose={async () => {
                    this.closeDrawer();
                    if (this.props.isRecording) {
                      await this.stopRecording(true);
                    }
                  }}
                />

                <div className="rightColumn">
                  <div className="mobileContentView">
                    <div className="contentViewText">
                      <Typography className="mobilePageTitle">
                        {!this.isOnComprehensionQuestion() &&
                          (this.props.currentPage?.title || `Page ${this.props.currentPageNumber + 1}`)}
                      </Typography>

                      {this.props.assignmentState.pages && this.props.currentQuestionId === "" && (
                        <ReaderView
                          pdfPath={this.props.assignmentState.book.pdfPath}
                          fontSize={Math.max(
                            (this.props.currentUserState.currentUserSettings?.readerFontSize || 28) as number,
                            8,
                          )}
                          viewType={this.props.assignmentState.book.viewType}
                          bookName={this.props.assignmentState.book.name}
                          pageNumber={this.props.currentPageNumber}
                          page={this.props.assignmentState.pages[this.props.currentPageNumber]}
                          correctString={
                            !this.props.isRecording ? this.props.currentRecordedPage?.unreadPortionWithErrors : ""
                          }
                        />
                      )}

                      {this.isOnComprehensionQuestion() && (
                        <div className="mobileQuestionView">
                          <Typography className="comprehensionTitle">
                            {this.props.currentQuestionId !== "" && "Comprehension Question"}
                          </Typography>

                          <ComprehensionQuestionView
                            classId={this.classId}
                            assignmentId={this.props.assignmentState?.book?.id}
                            currentUserId={this.props?.currentUserState?.currentUser?.uid}
                            replyType={this.props.assignmentState.assignment?.assignmentComments?.replyType}
                            question={
                              this.props.assignmentState.assignment?.assignmentComments?.comprehensionQuestions?.find(
                                question => question.id === this.props.currentQuestionId,
                              ) as ComprehensionQuestion
                            }
                          />
                        </div>
                      )}
                    </div>

                    {this.props.assignmentState.pages && this.props.currentQuestionId === "" && (
                      <ReadingPaneButtons
                        isRecording={this.props.isRecording}
                        isLastPage={this.isLastPage()}
                        hasRecordedPage={this.state.hasRecordedThisPage}
                        onDelete={() => {
                          this.openConfirmDelete(true);
                        }}
                        onNext={async () => {
                          await this.onNext();
                        }}
                        onSubmit={async () => {
                          this.closeDrawer();
                          await this.onSubmit();
                        }}
                      />
                    )}
                  </div>
                </div>
              </Drawer>
            </Hidden>
          </div>
        </div>
        {/* Modals */}
        <ConfirmationModal
          title="Delete Recording?"
          onOk={async () => {
            this.closeConfirmDelete();
            await this.deleteRecording();
            if (this.state.deletePromptContinueRecording) {
              await this.startRecording();
            }
          }}
          onCancel={() => {
            this.closeConfirmDelete();
          }}
          open={this.state.deletePromptOpen}
        >
          Do you want to delete this recording?
        </ConfirmationModal>
        <InformationModal
          title={this.props.informationModalState.title}
          onOk={closeInformationModal}
          open={this.props.informationModalState.isOpen}
        >
          {this.props.informationModalState.message}
        </InformationModal>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    ...state.bookScreenState,
    homeScreenState: state.studentHomeScreenState,
    userId: state.currentUserState.currentUser?.uid || "",
    audioDevice: state.currentUserState.currentUserSettings?.audioDevice,
    currentUserState: state.currentUserState,
    informationModalState: state.informationModalState,
    adminId: state.readleeSettingsState.adminId,
  };
};

export default withStyles(styles)(connect(mapStateToProps)(withSnackbar(withRouter(BookScreen))));
